import { invokeFunction } from '@/lib/supabase';
import type { SuggestedSender } from '@/pages/onboarding/StepAddSenders';

/** Matches MIN_QUERY_LENGTH in the edge function. */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Thrown when there is no Google access token to search with.
 *
 * Distinct from a search failure because the cause and the advice differ: the
 * mailbox was never queried, and no amount of retrying the field will help
 * until the Gmail session is re-established.
 *
 * Reached more easily than it looks. Supabase's `_getSessionFromURL` builds the
 * session WITH `provider_token`, but `_callRefreshToken` saves the refresh-grant
 * response, which carries none — so the first token refresh mid-onboarding
 * empties it. `StepApproveSenders` deliberately supports a mid-onboarding
 * reload, so a parent who leaves the tab open hits this.
 */
export class MissingGmailAccessTokenError extends Error {
  constructor() {
    super('No Google access token available to search the mailbox');
    this.name = 'MissingGmailAccessTokenError';
  }
}

/**
 * Searches the user's mailbox for senders matching `query`.
 *
 * A short query short-circuits without a network call — a search-as-you-type
 * field would otherwise fire a mailbox query per keystroke. Everything else
 * THROWS rather than returning []: an empty list reads as "no matches" and
 * would recreate the dead end this replaces.
 *
 * That reasoning was already written here, and a missing token was doing the
 * exact thing it warns against — returning [] and letting the dropdown fall
 * back to the in-memory scan with no sign anything was wrong.
 */
export async function searchSenders(
  accessToken: string,
  query: string,
): Promise<SuggestedSender[]> {
  const trimmed = query.trim();
  // Checked before the token: the user simply hasn't typed enough yet, and
  // reporting a session problem at one character would be noise.
  if (trimmed.length < MIN_SEARCH_LENGTH) return [];
  if (!accessToken) throw new MissingGmailAccessTokenError();

  const { data, error } = await invokeFunction('onboarding-search-senders', {
    body: { access_token: accessToken, query: trimmed },
  });
  if (error) throw error;
  return (data as { senders: SuggestedSender[] }).senders ?? [];
}

/**
 * Settings → Approved Senders search (YAR-502). Same edge function and the
 * same mailbox query as onboarding, but authorised by the household's stored
 * Gmail grant: the OAuth provider_token is long gone by the time a parent
 * returns to Settings. `needsOauth` means there is no usable grant, so the
 * caller can fall back instead of reading an empty list as "no matches".
 */
export async function searchHouseholdSenders(
  householdId: string,
  query: string,
): Promise<{ senders: SuggestedSender[]; needsOauth: boolean }> {
  const trimmed = query.trim();
  if (trimmed.length < MIN_SEARCH_LENGTH) {
    return { senders: [], needsOauth: false };
  }
  const { data, error } = await invokeFunction('onboarding-search-senders', {
    body: { household_id: householdId, query: trimmed },
  });
  if (error) throw error;
  const body = data as { senders?: SuggestedSender[]; needs_oauth?: boolean };
  return { senders: body.senders ?? [], needsOauth: body.needs_oauth === true };
}

/**
 * The senders of the parent's recent mail, fetched once for the dropdown to
 * match locally as they type (YAR-374). Gmail search matches whole words, so
 * a partial "merc" never reaches `@mercadolibre.com` through `searchSenders`;
 * a substring match over this list does. Pass the onboarding `accessToken`
 * or, from Settings, the `householdId`. Resolves `[]` when Settings has no
 * usable Gmail grant — the typed search reports that case on its own.
 */
export async function fetchRecentSenders(
  auth: { accessToken: string } | { householdId: string },
): Promise<SuggestedSender[]> {
  const body =
    'accessToken' in auth
      ? { access_token: auth.accessToken, mode: 'recent' }
      : { household_id: auth.householdId, mode: 'recent' };
  const { data, error } = await invokeFunction('onboarding-search-senders', {
    body,
  });
  if (error) throw error;
  return (data as { senders?: SuggestedSender[] }).senders ?? [];
}
